import React, { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FaStar, FaExternalLinkAlt } from 'react-icons/fa';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useTranslation } from 'react-i18next';

import googleIcon from '../../Assets/img/google1.webp';
import WithBackground from '../Common/WithBackground';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import './style.css';

const REVIEW_COUNT = 15;
const CLAMP_LINES = 4;

const ReviewCard = ({ name, text, showMoreLabel, showLessLabel }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }
  }, [text]);

  return (
    <div className='review-card'>
      <div className='review-card__header'>
        <img
          src={googleIcon}
          alt='Google'
          className='review-card__google-icon'
        />
        <div className='review-card__stars'>
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} className='review-card__star' />
          ))}
        </div>
      </div>
      <h3 className='review-card__name'>{name}</h3>
      <p
        ref={textRef}
        className={`review-card__text ${expanded ? 'review-card__text--expanded' : ''}`}
        style={{
          WebkitLineClamp: expanded ? 'unset' : CLAMP_LINES,
        }}
      >
        {text}
      </p>
      {isClamped && (
        <button
          className='review-card__toggle'
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  );
};

ReviewCard.propTypes = {
  name: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  showMoreLabel: PropTypes.string.isRequired,
  showLessLabel: PropTypes.string.isRequired,
};

const GoogleReviews = ({ bg, overlay }) => {
  const { t } = useTranslation();

  const reviews = [];
  for (let i = 1; i <= REVIEW_COUNT; i++) {
    reviews.push({
      id: i,
      name: t(`googleReviews.reviews.${i}.name`),
      text: t(`googleReviews.reviews.${i}.text`),
    });
  }

  const componentContent = (
    <section className='google-reviews' id='google-reviews'>
      <div className='google-reviews__container'>
        <div className='google-reviews__header'>
          <h2 className='google-reviews__title'>{t('googleReviews.title')}</h2>
          <p className='google-reviews__subtitle'>
            {t('googleReviews.subtitle')}
          </p>
        </div>

        <div className='google-reviews__slider'>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            navigation={{
              prevEl: '.google-reviews__prev',
              nextEl: '.google-reviews__next',
            }}
            pagination={{
              el: '.google-reviews__pagination',
              clickable: true,
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            breakpoints={{
              1024: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
            }}
            className='google-reviews__swiper'
          >
            {reviews.map((review) => (
              <SwiperSlide key={review.id}>
                <ReviewCard
                  name={review.name}
                  text={review.text}
                  showMoreLabel={t('googleReviews.showMore')}
                  showLessLabel={t('googleReviews.showLess')}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className='google-reviews__navigation'>
            <button className='google-reviews__prev' aria-label='Previous'>
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M15 18L9 12L15 6'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>

            <button className='google-reviews__next' aria-label='Next'>
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M9 18L15 12L9 6'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>

          <div className='google-reviews__pagination'></div>
        </div>

        <div className='google-reviews__cta'>
          <a
            href='https://share.google/1HISOudD2Yw2yr2Qj'
            className='google-reviews__cta-link'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={googleIcon}
              alt='Google'
              className='google-reviews__cta-icon'
            />
            <span>{t('googleReviews.seeAllReviews')}</span>
            <FaExternalLinkAlt className='google-reviews__cta-arrow' />
          </a>
        </div>
      </div>
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {componentContent}
    </WithBackground>
  ) : (
    componentContent
  );
};

GoogleReviews.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

export default GoogleReviews;
